import { css } from "@stitches/react";
import { TimePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

type TimePickerInputProps = {
  /** Lands on the inner input, so a <label htmlFor> can point at it. */
  id?: string;
  value?: Dayjs;
  format?: string;
  placeholder?: string;
  onChange(time: Dayjs, timeString: string | string[]): void;
  hideTimezone?: boolean;
};

export function TimePickerInput({
  id,
  value,
  format = "HH:mm",
  placeholder,
  onChange,
  hideTimezone = false,
}: TimePickerInputProps) {
  // Get user's timezone for display
  const userTimezone = dayjs.tz.guess();
  const timezoneAbbr = dayjs().tz(userTimezone).format("z");

  // Get full timezone name (e.g., "Europe/Dublin")
  const timezoneName = userTimezone.split("/").pop()?.replace(/_/g, " ") || "";

  // Create descriptive timezone label
  const timezoneLabel = `${timezoneAbbr} - ${timezoneName}`;

  // Force 24-hour format
  let displayFormat = format;
  if (format) {
    // Replace 12-hour format indicators with 24-hour
    displayFormat = format
      .replace(/\bh{1,2}\b/g, "HH")
      .replace(/\s*[Aa]\s*/g, "");
  }

  // Add timezone abbreviation to the display format (unless hidden)
  const displayFormatWithTz = hideTimezone
    ? displayFormat
    : `${displayFormat} [${timezoneAbbr}]`;
  const timePickerInputCss = css({
    height: "42px",
    fontFamily: "$openSans !important",
    border: "2px solid $gray-50 !important",

    "&:hover": {
      border:
        "2px solid var(--colors-portal-time-picker-hover-border) !important",
    },

    "&.ant-picker-focused": {
      border:
        "2px solid var(--colors-portal-time-picker-focus-border) !important",
      boxShadow: "0 0 0 2px rgba(189,180,221, 0.1) !important",
    },
  });

  const timePickerPopupCss = css({
    zIndex: 3100,
    pointerEvents: "all !important",
    fontFamily: "$openSans !important",

    span: {
      fontFamily: "$openSans !important",
    },

    // Add timezone indicator at top of popup (unless hidden)
    ...(!hideTimezone && {
      ".ant-picker-panel-container::before": {
        content: `"${timezoneLabel}"`,
        display: "block",
        padding: "8px 12px",
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #d9d9d9",
        fontSize: "12px",
        color: "#666",
        fontWeight: "500",
        textAlign: "center",
      },
    }),

    ".ant-picker-time-panel-column": {
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },

    ".ant-picker-time-panel-cell-selected ": {
      ".ant-picker-time-panel-cell-inner": {
        background:
          "var(--colors-portal-time-picker-popup-selected-cell) !important",
      },
    },

    ".ant-picker-time-panel-column::after": {
      height: "auto",
    },

    ".ant-picker-ranges": {
      marginTop: "0",
      fontWeight: "400",

      ".ant-picker-now-btn": {
        color: "#212121",

        "&:hover": {
          opacity: "0.8",
        },
      },

      ".ant-picker-ok": {
        button: {
          "&.ant-btn-primary": {
            backgroundColor: "#7562A8",

            "&:hover": {
              backgroundColor: "#615492",
            },
          },
        },
      },
    },
  });

  return (
    <TimePicker
      id={id}
      defaultValue={value}
      placeholder={placeholder}
      needConfirm={false}
      value={value}
      format={displayFormatWithTz}
      onChange={onChange}
      suffixIcon={null}
      popupClassName={timePickerPopupCss()}
      className={timePickerInputCss()}
      use12Hours={false}
    />
  );
}
